import React, { useEffect, useState, useRef } from 'react';
import Styles from 'global/styles';
import { get, set, sortBy, take } from 'global/lib/lodash';
import { useWindowSize } from 'global/hooks/useWindowSize';
import clsx from 'clsx';
import ModalGateway from 'global/components/ModalGateway';
import { CircularProgress, Icon, IconButton } from '@mui/material';
import { HorizontalScroll } from '../horizontal-scroll';
import { stopProp, _size } from '../../utils';
import { Button } from '../Button';
import CheckBoxOutlineBlankOutlined from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';
import Image from './Image';
import { FullScreenMedia, Media } from '../Media';
import { Row } from '../row';

const ProcessingIcon = () => {
  const styles = Styles();

  return (
    <div className={styles.processingContainer}>
      <CircularProgress />
      <div className="processText">Processing Image...</div>
    </div>
  );
};

const EImage = (props) => {
  const contRef = useRef();
  const styles = Styles();
  const unsetHeight = get(props, 'unsetHeight', false);
  const unsetWidth = get(props, 'unsetWidth', false);
  const [dims, setDims] = useState();
  const [src, setSrc] = useState(props.src);
  const [isProcessing, setIsProcessing] = useState(false);
  const ref = useRef({
    error: '',
    errorCount: 0,
    setBackup: false,
    timeout: null,
  });
  let width = unsetWidth ? null : get(props, 'width', 110);
  let height = unsetHeight ? null : get(props, 'height', width);

  const autoReload = get(props, 'autoReload', true);
  const autosize = get(props, 'autosize', 1);
  const useNaturalDims = get(props, 'useNaturalDims', true);
  const canSelect = get(props, 'canSelect', false);
  const imageSel = get(props, 'imageSel', false);

  const windowDims = useWindowSize();

  const setRef = (sel, setting) => {
    set(ref, `current.${sel}`, setting);
  };

  const getRef = (sel, fb) => {
    return get(ref, `current.${sel}`, fb);
  };

  const calc = (force = false) => {
    if (autosize || force) {
      const node = contRef.current;
      if (!node) {
        return;
      }
      const parent = node.parentNode;
      const innerWidth = parent.clientWidth * (autosize || 1);
      const img = node.getElementsByTagName('img')[0];
      if (img) {
        const imageMult = img.clientHeight / img.clientWidth;

        if (useNaturalDims) {
          setDims({
            width: unsetWidth ? null : img.naturalWidth,
            height: unsetHeight ? null : img.naturalHeight,
          });
        } else {
          setDims({
            width: unsetWidth ? null : innerWidth,
            height: unsetHeight ? null : innerWidth * (isNaN(imageMult) ? 1 : imageMult),
          });
        }
      }
    }
  };

  const startLoadCheck = () => {
    if (!getRef('timeout')) {
      const srcCopy = src;
      setSrc('');
      setIsProcessing(true);
      setRef(
        'timeout',
        setTimeout(() => {
          setRef('timeout', null);
          setSrc(srcCopy);
        }, 5000)
      );
    }
  };

  const onError = () => {
    setRef('errorCount', getRef('errorCount') + 1);
    const errorCount = getRef('errorCount');
    if (props.backupSrc && !getRef('setBackup')) {
      setRef('setBackup', true);
      setSrc(props.backupSrc);
    } else if (autoReload && errorCount < 10) {
      startLoadCheck();
    } else {
      get(props, 'onError', () => 0)();
    }
  };

  const selectImage = (e) => {
    stopProp(e);
    props.selectImage && props.selectImage(props);
  };

  const onLoad = () => {
    setTimeout(() => calc(), 500);
    setIsProcessing(false);
  };

  useEffect(() => {
    setSrc(props.src);
  }, [props.src]);

  useEffect(() => {
    calc();
  }, [autosize, windowDims.windowSize]);

  const hasCalced = !autosize ? true : !!dims || isProcessing;

  return (
    <div
      ref={contRef}
      className={clsx('IMAGE', styles.imageWrap, props.className)}
      style={{ ...(dims ? { ...dims } : { width, height }), ...(props.style || {}), ...(hasCalced ? { opacity: 1 } : { opacity: 0 }) }}
    >
      <Image {...props} imageStyle={{ ...((unsetHeight || unsetWidth) && { position: 'inherit' }), ...get(props, 'imageStyle', {}) }} src={src} onLoad={onLoad} onError={onError} loading={isProcessing ? <ProcessingIcon /> : undefined} />
      {canSelect && (
        <IconButton onClick={selectImage} className={clsx(styles.checkIcon, imageSel && styles.checkIconSelected)} size="large">
          {imageSel ? <CheckBoxOutlined /> : <CheckBoxOutlineBlankOutlined />}
        </IconButton>
      )}
    </div>
  );
};

export default EImage;

export const ImageList = (props) => {
  const { printView } = props;
  const styles = Styles();
  const [modal, setModal] = useState({
    open: false,
    onOpen: (index = 0) => setModal((m) => ({ ...m, index, open: true })),
    onClose: (s) => {
      setModal((m) => ({ ...m, open: false }));
    },
  });

  const imageSize = get(props, 'imageSize', printView ? 335 : 80);

  const images = sortBy(props.images || [], 'orderColumn').map((img, i) => ({
    opt: null,
    showCaption: true,
    imagetype: 'media',
    ...props,
    ...img,
    style: undefined,
    outerContainerStyle: { width: imageSize },
    index: i,
  }));
  const displayAsLink = get(props, 'displayAsLink', false);

  const clickImage = (e, img, index) => {
    if (props.onClick) {
      return props.onClick(e);
    }
    e.stopPropagation();
    e.preventDefault();
    modal.onOpen(index);
  };

  const spaceBetween = get(props, 'spaceBetween', printView ? 8 : 0);

  const displayAmt = get(props, 'displayAmt', Infinity);
  const selectedSize = (() => {
    const sel = props.selected;
    if (sel) {
      return Object.keys(sel).filter((k) => get(sel, k)).length;
    }
    return images.filter((img) => get(img, 'imageSel')).length;
  })();

  return (
    <Row onClick={(e) => e.stopPropagation()} dontCenter wrap>
      {!displayAsLink &&
        take(images, displayAmt).map((img, i) => {
          return (
            <div key={i} style={{ marginRight: displayAmt == 1 ? 0 : spaceBetween, marginBottom: spaceBetween }} className={clsx(styles.imageListItem, props.style)}>
              <Media
                {...img}
                printView={printView}
                sourceType={printView ? ['large', 'largeUrl', 'fullUrl', 'url'] : undefined}
                viewOnly={get(props, 'viewOnly', true)}
                images={images}
                selectMode={selectedSize > 0}
              />
            </div>
          );
        })}

      {displayAsLink && !!_size(images) && (
        <Button
          className={clsx(styles.linkSelect)}
          size="small"
          variant="text"
          color="secondary"
          startIcon={<Icon>photo_library</Icon>}
          onClick={(e) => clickImage(e, images[0], 0)}
        >
          {`${_size(images)} Image${_size(images) > 1 ? 's' : ''}`}
        </Button>
      )}

      {modal.open && <FullScreenMedia {...props} opt={null} images={images} {...modal} />}
    </Row>
  );
};

export const ImageCarousel = (props) => {
  const styles = Styles();
  const [modal, setModal] = useState(false);

  const images = sortBy(props.images || [], 'orderColumn');

  const clickImage = (e, img, index) => {
    if (props.onClick) {
      return props.onClick(e);
    }
    e.stopPropagation();
    e.preventDefault();
    setModal({ index });
  };

  useEffect(() => {
    if (!!modal) {
      props.onOpen && props.onOpen();
    } else {
      props.onClose && props.onClose();
    }
  }, [modal]);

  const getSrc = get(props, 'getSrc', (img) => get(img, 'full', img.thumb));

  const Source = (img) => {
    const hasLarge = get(img, 'largeUrl');
    const hasThumb = get(img, `thumbUrl`);
    const hasUrl = get(img, 'url');

    let avail = {
      full: hasLarge || hasUrl,
      thumb: hasThumb || hasUrl,
    };

    return getSrc(avail);
  };

  return (
    <div className={styles.imageCarousel}>
      <HorizontalScroll useImageScroller slidesToSlide={1}>
        {images.map((img, i) => {
          return (
            <div key={i} className={clsx('imageItem', props.style)}>
              <EImage key={i} className={styles.clickableImage} onClick={(e) => clickImage(e, img, i)} src={Source(img)} useNaturalDims={false} {...props} />
            </div>
          );
        })}
      </HorizontalScroll>

      <ModalGateway
        open={modal}
        onClose={() => setModal()}
        currentIndex={get(modal, 'index')}
        images={images.map((x) => ({
          ...x,
          key: x.id,
          src: Source(x),
          caption: props.caption ? props.caption(x) : x.caption || x.name,
        }))}
      />
    </div>
  );
};
